import React, { useCallback, useEffect, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { 
  $getSelection, 
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  $createParagraphNode,
  $getRoot
} from 'lexical';
import { 
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  ListNode
} from '@lexical/list';
import { 
  $isHeadingNode,
  $createHeadingNode,
  HeadingNode
} from '@lexical/rich-text';
import { 
  $setBlocksType,
  $getSelectionStyleValueForProperty,
  $patchStyleText
} from '@lexical/selection';
import { mergeRegister } from '@lexical/utils';
import { Box, ToggleButtonGroup, ToggleButton, IconButton, Select, MenuItem } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight
} from '@mui/icons-material';

const FONT_FAMILY_OPTIONS = [
  ['Arial', 'Arial'],
  ['Courier New', 'Courier New, monospace'],
  ['Georgia', 'Georgia, serif'],
  ['Times New Roman', 'Times New Roman, serif'],
  ['Trebuchet MS', 'Trebuchet MS, sans-serif'],
  ['Verdana', 'Verdana, sans-serif'],
];

const FONT_SIZE_OPTIONS = [
  ['8px', '8px'],
  ['9px', '9px'],
  ['10px', '10px'],
  ['11px', '11px'],
  ['12px', '12px'],
  ['13px', '13px'],
  ['14px', '14px'],
  ['16px', '16px'],
  ['18px', '18px'],
  ['20px', '20px'],
  ['22px', '22px'],
  ['24px', '24px'],
  ['26px', '26px'],
  ['28px', '28px'],
  ['32px', '32px'],
  ['36px', '36px'],
  ['48px', '48px'],
  ['72px', '72px'],
];

export const ToolbarPlugin = ({ mode, onModeChange, toolbarConfig = {} }) => {
  const [editor] = useLexicalComposerContext();
  const [activeEditor, setActiveEditor] = useState(editor);
  const [blockType, setBlockType] = useState('paragraph');
  const [fontSize, setFontSize] = useState('15px');
  const [fontFamily, setFontFamily] = useState('Arial');
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);

  const {
    showModeButtons = false,
    showBasicFormatting = true,
    showAdvancedFormatting = true,
    showListControls = true,
    showAlignmentControls = true,
    showFontControls = true,
  } = toolbarConfig;

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);

      // Update the text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsCode(selection.hasFormat('code'));

      // Update block type
      if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType(anchorNode, ListNode);
        const type = parentList
          ? parentList.getListType()
          : element.getListType();
        setBlockType(type);
      } else {
        const type = $isHeadingNode(element)
          ? element.getTag()
          : element.getType();
        if (type in blockTypeToBlockName) {
          setBlockType(type);
        }
      }

      // Update font family
      setFontFamily(
        $getSelectionStyleValueForProperty(selection, 'font-family', 'Arial'),
      );
      // Update font size
      setFontSize(
        $getSelectionStyleValueForProperty(selection, 'font-size', '15px'),
      );
    }
  }, [activeEditor]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        $updateToolbar();
        setActiveEditor(newEditor);
        return false;
      },
      1,
    );
  }, [editor, $updateToolbar]);

  useEffect(() => {
    return mergeRegister(
      activeEditor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
    );
  }, [$updateToolbar, activeEditor]);

  const formatText = useCallback(
    (format) => {
      activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
    },
    [activeEditor],
  );

  const formatParagraph = useCallback(() => {
    activeEditor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  }, [activeEditor]);

  const formatHeading = useCallback(
    (headingSize) => {
      if (blockType !== headingSize) {
        activeEditor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createHeadingNode(headingSize));
          }
        });
      }
    },
    [activeEditor, blockType],
  );

  const formatBulletList = useCallback(() => {
    if (blockType !== 'bullet') {
      activeEditor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      activeEditor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  }, [activeEditor, blockType]);

  const formatNumberedList = useCallback(() => {
    if (blockType !== 'number') {
      activeEditor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      activeEditor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  }, [activeEditor, blockType]);

  const formatAlignment = useCallback(
    (alignment) => {
      activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, alignment);
    },
    [activeEditor],
  );

  const onFontFamilySelect = useCallback(
    (value) => {
      activeEditor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, {
            'font-family': value,
          });
        }
      });
    },
    [activeEditor],
  );

  const onFontSizeSelect = useCallback(
    (value) => {
      activeEditor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, {
            'font-size': value,
          });
        }
      });
    },
    [activeEditor],
  );

  return (
    <Box sx={{ 
      display: 'flex', 
      flexWrap: 'wrap',
      alignItems: 'center', 
      gap: 1, 
      padding: 1,
      backgroundColor: '#f5f5f5',
      borderRadius: 1,
      border: '1px solid #ddd',
      marginBottom: 1
    }}>
      {showModeButtons && (
        <>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(e, value) => value && onModeChange(value)}
            size="small"
          >
            <ToggleButton value="question">Question</ToggleButton>
            <ToggleButton value="answer">Answer</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ width: '1px', height: '24px', backgroundColor: '#ddd', mx: 1 }} />
        </>
      )}

      {showBasicFormatting && (
        <>
          <IconButton
            size="small"
            onClick={() => formatText('bold')}
            sx={{ 
              backgroundColor: isBold ? '#1976d2' : 'transparent',
              color: isBold ? 'white' : 'inherit'
            }}
          >
            <FormatBold />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => formatText('italic')}
            sx={{ 
              backgroundColor: isItalic ? '#1976d2' : 'transparent',
              color: isItalic ? 'white' : 'inherit'
            }}
          >
            <FormatItalic />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => formatText('underline')}
            sx={{ 
              backgroundColor: isUnderline ? '#1976d2' : 'transparent',
              color: isUnderline ? 'white' : 'inherit'
            }}
          >
            <FormatUnderlined />
          </IconButton>
        </>
      )}

      {showListControls && (
        <>
          <Box sx={{ width: '1px', height: '24px', backgroundColor: '#ddd', mx: 1 }} />
          <IconButton
            size="small"
            onClick={formatBulletList}
            sx={{ 
              backgroundColor: blockType === 'bullet' ? '#1976d2' : 'transparent',
              color: blockType === 'bullet' ? 'white' : 'inherit'
            }}
          >
            <FormatListBulleted />
          </IconButton>
          <IconButton
            size="small"
            onClick={formatNumberedList}
            sx={{ 
              backgroundColor: blockType === 'number' ? '#1976d2' : 'transparent',
              color: blockType === 'number' ? 'white' : 'inherit'
            }}
          >
            <FormatListNumbered />
          </IconButton>
        </>
      )}

      {showAlignmentControls && (
        <>
          <Box sx={{ width: '1px', height: '24px', backgroundColor: '#ddd', mx: 1 }} />
          <IconButton size="small" onClick={() => formatAlignment('left')}>
            <FormatAlignLeft />
          </IconButton>
          <IconButton size="small" onClick={() => formatAlignment('center')}>
            <FormatAlignCenter />
          </IconButton>
          <IconButton size="small" onClick={() => formatAlignment('right')}>
            <FormatAlignRight />
          </IconButton>
        </>
      )}

      {showFontControls && (
        <>
          <Box sx={{ width: '1px', height: '24px', backgroundColor: '#ddd', mx: 1 }} />
          <Select
            value={fontFamily}
            onChange={(e) => onFontFamilySelect(e.target.value)}
            size="small"
            sx={{ minWidth: 120 }}
          >
            {FONT_FAMILY_OPTIONS.map(([option, text]) => (
              <MenuItem key={option} value={option}>
                <span style={{ fontFamily: text }}>{option}</span>
              </MenuItem>
            ))}
          </Select>
          <Select
            value={fontSize}
            onChange={(e) => onFontSizeSelect(e.target.value)}
            size="small"
            sx={{ minWidth: 80 }}
          >
            {FONT_SIZE_OPTIONS.map(([option, text]) => (
              <MenuItem key={option} value={option}>
                {text}
              </MenuItem>
            ))}
          </Select>
        </>
      )}
    </Box>
  );
};

// Helper functions
const blockTypeToBlockName = {
  bullet: 'Bulleted List',
  check: 'Check List',
  code: 'Code Block',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  h6: 'Heading 6',
  number: 'Numbered List',
  paragraph: 'Normal',
  quote: 'Quote',
};

function $findMatchingParent(node, findFn) {
  let curr = node;

  while (curr !== $getRoot() && curr != null) {
    if (findFn(curr)) {
      return curr;
    }

    curr = curr.getParent();
  }

  return null;
}

function $isRootOrShadowRoot(node) {
  return node === $getRoot() || (node != null && node.getKey() === 'root');
}

function $getNearestNodeOfType(node, klass) {
  let parent = node;

  while (parent != null) {
    if (parent instanceof klass) {
      return parent;
    }

    parent = parent.getParent();
  }

  return null;
}